package diana.soleil.design.iraniancanadienbazzar.feature.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.common.getOrNull
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CategoryRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ChatRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ImagePickController
import diana.soleil.design.iraniancanadienbazzar.core.data.FavoritesController
import diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.InterestsController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReviewRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfile
import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfileRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.koin.core.module.dsl.viewModel
import org.koin.dsl.module

/**
 * Real profile data (replaces the Phase-1 mock): the saved businesses resolved from the persisted
 * favorite ids, the caller's review count, and the editable name/bio/member-since read off the
 * account's `users/{uid}` doc. Identity (email/guest) still comes from the session via the shell —
 * this VM owns the repository-backed profile fields.
 */
data class ProfileDataUiState(
    val savedBusinesses: List<Business> = emptyList(),
    val followingBusinesses: List<Business> = emptyList(),
    val myReviewCount: Int = 0,
    val displayName: String? = null,
    val bio: String? = null,
    val memberSinceMillis: Long? = null,
    val pushEnabled: Boolean = true,
    val gemPushEnabled: Boolean = true,
    val emailMarketingConsented: Boolean = false,
    val isSaving: Boolean = false,
    /** True when the last push/email preference write failed — the screen shows an inline error. */
    val prefsWriteFailed: Boolean = false,
    /** True when the last profile-edit save failed — the editor stays open and shows an inline error. */
    val saveFailed: Boolean = false,
    /** Mahaleh Pro subscription active for this account. */
    val proActive: Boolean = false,
    /** Active Pro plan id (`pro_monthly`/`pro_annual`), or null. */
    val proPlan: String? = null,
)

class ProfileViewModel(
    private val favoritesController: FavoritesController,
    businessRepository: BusinessRepository,
    reviewRepository: ReviewRepository,
    followRepository: FollowRepository,
    private val userProfileRepository: UserProfileRepository,
    private val interestsController: InterestsController,
    categoryRepository: CategoryRepository,
    private val chatRepository: ChatRepository,
    private val imagePickController: ImagePickController,
    savedNewsController: diana.soleil.design.iraniancanadienbazzar.core.data.SavedNewsController,
    eventRepository: diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository,
) : ViewModel() {

    /**
     * Everything the user has kept, across all three collections — the number behind the "Saved" stat,
     * which now opens the Saved screen. Counted from the id sets only (no document reads): resolving
     * the models is the Saved screen's job, and the header just needs a truthful total.
     */
    val savedTotal: StateFlow<Int> = combine(
        favoritesController.favorites,
        savedNewsController.saved,
        eventRepository.observeMyRsvps().catch { emit(emptySet()) },
    ) { favorites, news, rsvps -> favorites.size + news.size + rsvps.size }
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), 0)


    /** The caller's public card (photo + self-disclosure fields) — prefills the profile editor. */
    private val myCardState = kotlinx.coroutines.flow.MutableStateFlow<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile?>(null)
    val myCard: StateFlow<diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile?> =
        myCardState

    /** A profile-photo upload is in flight (spinner on the avatar row). */
    private val photoSavingState = kotlinx.coroutines.flow.MutableStateFlow(false)
    val photoSaving: StateFlow<Boolean> = photoSavingState

    /** The last profile-photo upload failed (cleared on the next attempt) — the avatar row shows it. */
    private val photoUploadFailedState = kotlinx.coroutines.flow.MutableStateFlow(false)
    val photoUploadFailed: StateFlow<Boolean> = photoUploadFailedState

    /** One-shot: flips true after a successful profile save so the editor sheet can close. */
    private val profileSavedState = kotlinx.coroutines.flow.MutableStateFlow(false)
    val profileSaved: StateFlow<Boolean> = profileSavedState

    init {
        refreshCard()
    }

    private fun refreshCard() {
        viewModelScope.launch {
            myCardState.value = (chatRepository.myPublicProfile() as? DataResult.Success)?.data
        }
    }

    /**
     * Flip the Find-your-people opt-in from the Profile tab's public-card preview.
     *
     * Re-sends the CURRENT bio and intents so toggling visibility never silently wipes them —
     * `setDiscoveryProfile` writes the whole card. Refreshes so the preview reflects what was stored,
     * not what we hoped was stored.
     */
    fun setDiscoverable(discoverable: Boolean) {
        viewModelScope.launch {
            val card = myCardState.value
            chatRepository.setDiscoveryProfile(discoverable, card?.bio, card?.intents.orEmpty())
            refreshCard()
        }
    }

    /** Opens the platform image picker and uploads the pick as the profile photo. */
    fun changePhoto() {
        viewModelScope.launch {
            val image = imagePickController.pick() ?: return@launch
            photoSavingState.value = true
            photoUploadFailedState.value = false
            // Surface upload failure instead of silently stopping the spinner on the old avatar.
            when (chatRepository.setProfilePhoto(image)) {
                is DataResult.Success -> refreshCard()
                is DataResult.Failure -> photoUploadFailedState.value = true
            }
            photoSavingState.value = false
        }
    }

    /** Adds one photo to the public gallery (max [MAX_PROFILE_PHOTOS]); refreshes so the strip updates. */
    fun addPhoto() {
        viewModelScope.launch {
            val image = imagePickController.pick() ?: return@launch
            photoSavingState.value = true
            photoUploadFailedState.value = false
            when (chatRepository.addProfilePhoto(image)) {
                is DataResult.Success -> refreshCard()
                is DataResult.Failure -> photoUploadFailedState.value = true
            }
            photoSavingState.value = false
        }
    }

    /** Removes one gallery photo by URL. */
    fun removePhoto(url: String) {
        viewModelScope.launch {
            chatRepository.removeProfilePhoto(url)
            refreshCard()
        }
    }

    /** Clears the profile-photo upload error after the avatar row has shown it. */
    fun consumePhotoUploadFailed() {
        photoUploadFailedState.value = false
    }

    /** Clears the one-shot save-succeeded flag after the editor sheet has closed. */
    fun consumeProfileSaved() {
        profileSavedState.value = false
    }

    /** Starts a Mahaleh Pro subscription checkout; returns the Stripe URL for the screen to open. */
    suspend fun startProCheckout(planId: String): String? =
        (userProfileRepository.createSubscriptionCheckout(planId) as? DataResult.Success)?.data

    /** Opens the Stripe Billing Portal to manage/cancel Pro; returns its URL for the screen to open. */
    suspend fun openBillingPortal(): String? =
        (userProfileRepository.createBillingPortalSession() as? DataResult.Success)?.data

    /** Interest picks (from onboarding or edited here); synced to `users/{uid}.interests`. */
    val interests: StateFlow<Set<String>> = interestsController.interests

    /** The category taxonomy for the interests editor sheet, seeded order. */
    val interestOptions: StateFlow<List<diana.soleil.design.iraniancanadienbazzar.core.model.Category>> =
        categoryRepository.observeCategories()
            .map { result -> result.getOrNull().orEmpty().sortedBy { it.order } }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    fun toggleInterest(categoryId: String) = interestsController.toggle(categoryId)

    private val savingState = kotlinx.coroutines.flow.MutableStateFlow(false)

    /** Last push/email preference write failed (cleared on the next attempt) — see the toggles. */
    private val prefsWriteFailedState = kotlinx.coroutines.flow.MutableStateFlow(false)

    /** Last profile-edit save failed (cleared on the next attempt) — the editor stays open. */
    private val saveFailedState = kotlinx.coroutines.flow.MutableStateFlow(false)

    private val businessCache = mutableMapOf<String, Business>()

    /**
     * Saved/followed listings resolved BY ID (parallel point reads with a session cache), never by
     * filtering a streamed corpus — the old `observeBusinesses(metroId = null)` here held a live
     * listener on the entire national directory just to render these two lists, which was the
     * single most expensive listener in the app and scaled with the directory, not the user.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    private val resolvedLibrary: Flow<Pair<List<Business>, List<Business>>> =
        combine(favoritesController.favorites, followRepository.observeFollowing()) { favs, following ->
            favs to (following as? DataResult.Success)?.data.orEmpty()
        }.mapLatest { (favoriteIds, followingIds) ->
            val missing = (favoriteIds + followingIds) - businessCache.keys
            if (missing.isNotEmpty()) {
                businessRepository.getBusinesses(missing).forEach { businessCache[it.id] = it }
            }
            favoriteIds.mapNotNull(businessCache::get) to followingIds.mapNotNull(businessCache::get)
        }

    val state: StateFlow<ProfileDataUiState> =
        combine(
            resolvedLibrary,
            reviewRepository.observeMyReviews(),
            userProfileRepository.observeProfile(),
            savingState,
            prefsWriteFailedState,
            saveFailedState,
        ) { flows ->
            @Suppress("UNCHECKED_CAST")
            val (saved, following) = flows[0] as Pair<List<Business>, List<Business>>
            val myReviews = (flows[1] as DataResult<List<*>>).getOrNull().orEmpty()
            val profile = (flows[2] as DataResult<UserProfile?>).getOrNull()
            val isSaving = flows[3] as Boolean
            val prefsFailed = flows[4] as Boolean
            val saveFailed = flows[5] as Boolean
            ProfileDataUiState(
                savedBusinesses = saved,
                followingBusinesses = following,
                myReviewCount = myReviews.size,
                displayName = profile?.displayName,
                bio = profile?.bio,
                memberSinceMillis = profile?.createdAtMillis,
                pushEnabled = profile?.pushEnabled ?: true,
                gemPushEnabled = profile?.gemPushEnabled ?: true,
                emailMarketingConsented = profile?.emailMarketingConsented ?: false,
                isSaving = isSaving,
                prefsWriteFailed = prefsFailed,
                saveFailed = saveFailed,
                proActive = profile?.proActive ?: false,
                proPlan = profile?.proPlan,
            )
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ProfileDataUiState())

    /** Unsave from the "Saved Listings" heart; the list updates live via the favorites flow. */
    fun toggleSaved(businessId: String) = favoritesController.toggle(businessId)

    /** Persists the push-notification opt-in to `users/{uid}.pushEnabled` (Functions read it server-side). */
    fun setPushEnabled(enabled: Boolean) {
        viewModelScope.launch {
            prefsWriteFailedState.value = false
            if (userProfileRepository.setPushEnabled(enabled) is DataResult.Failure) {
                prefsWriteFailedState.value = true
            }
        }
    }

    /** Persists the daily-gem opt-in to `users/{uid}.gemPushEnabled` (read by `dailyGemPush`). */
    fun setGemPushEnabled(enabled: Boolean) {
        viewModelScope.launch {
            prefsWriteFailedState.value = false
            if (userProfileRepository.setGemPushEnabled(enabled) is DataResult.Failure) {
                prefsWriteFailedState.value = true
            }
        }
    }

    /**
     * Records CASL express consent (or withdrawal) for marketing emails on
     * `users/{uid}.emailMarketing`; the email campaign channel only targets consented users.
     * A dropped failure here silently un-records a legal consent choice — it must surface.
     */
    fun setEmailMarketingConsent(consented: Boolean) {
        viewModelScope.launch {
            prefsWriteFailedState.value = false
            if (userProfileRepository.setEmailMarketingConsent(consented) is DataResult.Failure) {
                prefsWriteFailedState.value = true
            }
        }
    }

    /**
     * Merge-writes the edited name + bio to `users/{uid}` and the public self-disclosure fields
     * (occupation/hometown/relationship/languages/interests) to `publicProfiles/{uid}`.
     */
    fun saveProfile(
        displayName: String,
        bio: String,
        city: String? = null,
        occupation: String? = null,
        hometown: String? = null,
        relationship: String? = null,
        languages: List<String> = emptyList(),
        interests: List<String> = emptyList(),
    ) {
        viewModelScope.launch {
            savingState.value = true
            saveFailedState.value = false
            // Capture BOTH writes — silently dropping a failure closed the sheet over unsaved edits.
            val nameOk = userProfileRepository.updateProfile(displayName.trim(), bio.trim()) is DataResult.Success
            val socialOk = chatRepository.setSocialProfile(city, occupation, hometown, relationship, languages, interests) is DataResult.Success
            refreshCard()
            savingState.value = false
            if (nameOk && socialOk) profileSavedState.value = true else saveFailedState.value = true
        }
    }
}

/** Koin bindings for the profile feature. Aggregated by the app composition root (`:shared`). */
val profileModule = module {
    viewModel { ProfileViewModel(get(), get(), get(), get(), get(), get(), get(), get(), get(), get(), get()) }
    viewModel { BlockedUsersViewModel(get()) }
    viewModel { SavedViewModel(get(), get(), get(), get(), get()) }
}
